/**
 * Diagnostic codes: the short string a coach reads off the screen and sends on.
 *
 * `packages/schema` fixes the shape at exactly six characters and the handoff
 * gives the example `SB-114`, so: two letters naming the surface, a hyphen, and
 * three characters identifying the failure.
 *
 * A crash inside the app has no server log entry to point at, so the three
 * characters are derived from the error itself rather than generated randomly.
 * That buys something a random code does not: the same crash produces the same
 * code on every device, so three parents reporting `UI-K2P` are reporting one
 * bug rather than three.
 *
 * Nothing derived from the error is ever shown to a user. The hash is one way
 * and three characters wide; the message, the stack, and the exception type
 * stay inside this module.
 */

/** Six characters, matching `ApiErrorSchema.diagnostic`. */
const GENERATED_PATTERN = /^[A-Z]{2}-[0-9A-Z]{3}$/;
const SURFACE_PATTERN = /^[A-Z]{2}$/;
const DIAGNOSTIC_LENGTH = 6;

/** Surface prefix used when a caller does not name one. */
export const DEFAULT_SURFACE = 'UI';

/** True for a code this module could have generated. Used by the tests. */
export function isGeneratedDiagnostic(value: string): boolean {
  return GENERATED_PATTERN.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Pull a diagnostic the server already issued out of a thrown value.
 *
 * Accepts either a thrown `ApiError` or the whole `{ error: { ... } }`
 * envelope. Anything six characters long is taken as-is, because the server
 * owns its own format and a client that reformats it breaks the link between
 * the code on screen and the code in the log.
 */
export function carriedDiagnostic(error: unknown): string | null {
  if (!isRecord(error)) return null;

  const direct = error['diagnostic'];
  if (typeof direct === 'string' && direct.length === DIAGNOSTIC_LENGTH) return direct;

  const envelope = error['error'];
  if (isRecord(envelope)) {
    const nested = envelope['diagnostic'];
    if (typeof nested === 'string' && nested.length === DIAGNOSTIC_LENGTH) return nested;
  }

  return null;
}

/** FNV-1a, folded to three base 36 characters. */
function fingerprint(input: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  // 36^3, so the result is always three characters after padding.
  return (hash % 46656).toString(36).toUpperCase().padStart(3, '0');
}

function describe(error: unknown): string {
  if (error instanceof Error) return `${error.name}:${error.message}`;
  if (typeof error === 'string') return `String:${error}`;
  return `Unknown:${typeof error}`;
}

/**
 * The code to show for a caught error: the server's if it carried one, a
 * deterministic one derived from the error otherwise.
 */
export function diagnosticFor(error: unknown, surface: string = DEFAULT_SURFACE): string {
  const carried = carriedDiagnostic(error);
  if (carried !== null) return carried;

  const prefix = SURFACE_PATTERN.test(surface) ? surface : DEFAULT_SURFACE;
  return `${prefix}-${fingerprint(describe(error))}`;
}

/**
 * The words in front of a code on every refusal and error screen (#82, #106,
 * ADR-0100). One spelling, so support hears the same request from every
 * screen and a coach knows the string after it is the thing to send.
 */
export const SUPPORT_CODE_LEAD = 'Quote this code to support:';

/** The whole support line, as one sentence that can close a paragraph. */
export function supportCodeLine(code: string): string {
  return `${SUPPORT_CODE_LEAD} ${code}.`;
}
